import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { MediaItem } from '@/types/posts';
import { VideoPlayerModal } from '@/components/ui/VideoPlayerModal';
import { usePlaceholderImage } from '@/hooks/usePlaceholderImage';
import { BlurFillImage } from './BlurFillImage';
import { durationLabel } from '../previewMedia';
import { CarouselChevron } from './CarouselChevron';
import { focusSurface } from '@/components/ui/focusStyles';
import { PdfPageViewer } from '@/components/ui/PdfPageViewer';

interface MediaDisplayProps {
  media: MediaItem[];
  aspectRatio?: 'square' | 'video' | 'portrait' | 'auto';
  rounded?: 'none' | 'lg' | '2xl';
  showCarouselDots?: boolean;
  /** Optional controlled slide index (uncontrolled when omitted) */
  activeIndex?: number;
  onIndexChange?: (index: number) => void;
}

/**
 * Media Display Component - handles single image, carousel, or video
 */
export const MediaDisplay = ({
  media,
  aspectRatio = 'square',
  rounded = 'none',
  showCarouselDots = true,
  activeIndex,
  onIndexChange,
}: MediaDisplayProps) => {
  const PLACEHOLDER_IMAGE = usePlaceholderImage();
  const { t } = useTranslation('common');
  const [innerIndex, setInnerIndex] = useState(0);
  const isControlled = activeIndex !== undefined;
  const currentIndex = Math.min(isControlled ? activeIndex : innerIndex, Math.max(0, media.length - 1));
  const setCurrentIndex = (next: number | ((prev: number) => number)): void => {
    const value = typeof next === 'function' ? next(currentIndex) : next;
    if (!isControlled) setInnerIndex(value);
    onIndexChange?.(value);
  };
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);

  if (media.length === 0) return null;

  const current: MediaItem | undefined = media[currentIndex];

  const aspectClass = {
    square: 'aspect-square',
    video: 'aspect-video',
    portrait: 'aspect-[9/16]',
    auto: '',
  }[aspectRatio];

  const roundedClass = {
    none: '',
    lg: 'rounded-lg',
    '2xl': 'rounded-2xl',
  }[rounded];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <div className="relative">
        <div className={cn('bg-gray-100 overflow-hidden', aspectClass, roundedClass)}>
          {current?.type === 'image' ? (
            aspectRatio === 'portrait' ? (
              <BlurFillImage
                src={current.url}
                alt={`Post media ${currentIndex + 1}`}
                className="h-full w-full"
              />
            ) : (
              <img
                src={current.url}
                alt={`Post media ${currentIndex + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                }}
              />
            )
          ) : current?.type === 'video' ? (
            <div className={cn('relative w-full bg-gray-900', aspectClass ? 'h-full' : 'max-h-80')}>
              {/* Video poster frame */}
              {current.thumbnail ? (
                aspectClass ? (
                  <BlurFillImage src={current.thumbnail} alt="Video preview" className="h-full w-full" />
                ) : (
                  <img
                    src={current.thumbnail}
                    alt="Video preview"
                    className={cn('w-full max-h-80 object-contain')}
                  />
                )
              ) : (
                <video
                  src={current.url}
                  className={cn('w-full object-contain', aspectClass ? 'h-full' : 'max-h-80')}
                  controls={false}
                  muted
                  playsInline
                />
              )}
              <button
                type="button"
                onClick={() => setPlayingVideoUrl(current.url)}
                className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors cursor-pointer group"
                aria-label={t('play_video')}
              >
                <div className="w-12 h-12 bg-white/90 group-hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors">
                  <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[13px] border-l-gray-800 border-b-[8px] border-b-transparent ml-1" />
                </div>
              </button>
              {durationLabel(current.duration) && (
                <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[11px] px-1.5 py-0.5 rounded font-medium pointer-events-none">
                  {durationLabel(current.duration)}
                </div>
              )}
            </div>
          ) : current?.type === 'pdf' ? (
            <div className={cn('relative flex w-full flex-col bg-gray-100', aspectClass ? 'h-full' : 'min-h-48')}>
              <PdfPageViewer
                key={current.url}
                url={current.url}
                filename={current.filename}
                thumbnailUrl={current.thumbnail}
                aspectRatioHint={current.width && current.height ? current.width / current.height : undefined}
                variant="preview"
                fitToFrame={Boolean(aspectClass)}
                className={aspectClass ? 'min-h-0 flex-1' : undefined}
              />
              <div className="flex flex-shrink-0 items-center gap-2 border-t border-gray-200 bg-white px-3 py-2">
                <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700">
                  {'PDF'}
                </span>
                <span className="min-w-0 truncate text-xs text-gray-800">
                  {current.filename || t('release_media_document', { defaultValue: 'Document' })}
                </span>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-900">
              <div className="text-center">
                <Icon name={ICONS.IMAGE} size="xl" color="text-white" />
                <p className="text-white text-xs mt-2">{t('release_media_fallback', { defaultValue: 'Media' })}</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Arrows for Carousel */}
        {media.length > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className={`absolute left-2 top-1/2 -translate-y-1/2 flex h-[24px] w-[24px] aspect-square items-center justify-center rounded-full bg-white/85 text-gray-700 shadow transition-colors before:absolute before:-inset-2.5 before:content-[''] hover:bg-white ${focusSurface}`}
              aria-label={t('previous')}
            >
              <CarouselChevron direction="left" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className={`absolute right-2 top-1/2 -translate-y-1/2 flex h-[24px] w-[24px] aspect-square items-center justify-center rounded-full bg-white/85 text-gray-700 shadow transition-colors before:absolute before:-inset-2.5 before:content-[''] hover:bg-white ${focusSurface}`}
              aria-label={t('next')}
            >
              <CarouselChevron direction="right" />
            </button>
          </>
        )}

        {/* Carousel Dots */}
        {showCarouselDots && media.length > 1 && current?.type !== 'pdf' && (
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1 drop-shadow">
            {media.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentIndex(idx)}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-colors duration-150',
                  idx === currentIndex ? 'bg-white' : 'bg-white/50'
                )}
                aria-label={t('go_to_slide', { number: idx + 1 })}
              />
            ))}
          </div>
        )}

        {/* Carousel Counter */}
        {media.length > 1 && (
          <div className="absolute top-2.5 right-2.5 bg-black/60 text-white text-[11px] px-2 py-0.5 rounded-full font-medium">
            {currentIndex + 1}/{media.length}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={!!playingVideoUrl}
        onClose={() => setPlayingVideoUrl(null)}
        videoUrl={playingVideoUrl || ''}
        title={media[currentIndex]?.filename}
      />
    </>
  );
};
